import React, { useState, useEffect, useRef } from "react";
import { StyledGraceCTA } from "./styles";
import Subscription from "../../../models/Subscription";
import { useAppStore } from "../../../stores/appStore";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { AppEventPublisher } from "../../../AppEventPublisher";
import classNames from "classnames";
import { isDarkMode } from "../../../Utils";
import dateFormat from "dateformat";
import { sendAppEvent } from "../../../Utils";

const getGraceMessage = (subscription, subscriptionData, consumable) => {
  if (subscription.activeTrial) {
    return (
      <p>You've run out of {consumable} for your free trial. We're giving a few more so you can finish what you're working on before you need to sign up.</p>
    );
  } else if (subscription.data.graceAmount < 250_000) {
    return (
      <p>
        <strong>Free {consumable}!</strong> You've run out of {consumable} for your current period.
        <br />
        We're giving you a few more so you can finish up what you're working on.
      </p>
    );
  } else {
    const refreshDate = subscriptionData && subscriptionData.nextProvisionDate() ? dateFormat(subscriptionData.nextProvisionDate(), "mmm d") : null;
    return (
      <p>
        Hey, we saw you just ran out of {consumable}! Folks sometimes run out of {consumable} quickly in their first month because they're experimenting and learning.
        <br />
        <strong>So here's an extra {subscription.data.graceAmount.toLocaleString()} {consumable} to use before your plan refreshes{refreshDate ? ` on ${refreshDate}` : ""}.</strong> Enjoy!
      </p>
    );
  }
};

const GraceCTAComponent = ({ subscription, uid, userData }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isSW2 = useAppStore((state) => state.isSW2);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const steps = useStickyTourStore((state) => state.steps);
  const activeTourVariation = useStickyTourStore((state) => state.activeTourVariation);

  useEffect(() => {
    // this is so we can use the model method to claim grace
    // and to determine their refresh date
    const sub = new Subscription(uid);
    sub.fetchAndListen((sub) => {
      setSubscriptionData(sub);
      sendAppEvent("grace_cta_viewed", {
        active_trial: sub.activeTrial,
        grace_amount: sub.data.graceAmount,
      });
    });
  }, []);

  let coinsURL = useRef("/assets/pricing/wordcoin.png");

  useEffect(() => {
    coinsURL.current = isDarkMode(userData?.data?.theme) ? "/assets/pricing/wordcoin-shadowless.png" : "/assets/pricing/wordcoin.png";
  }, [userData?.data?.theme]);

  const handleClaimGrace = async (e) => {
    e.preventDefault();
    setLoading(true);

    const graceAmount = subscription.data.graceAmount;
    const tourContext = activeTourId
      ? { tourId: activeTourId, stepId: steps[currentStepIndex]?.id, stepIndex: currentStepIndex, variation: activeTourVariation }
      : undefined;

    await subscriptionData.claimGrace();

    AppEventPublisher.credits.claimedBonus(graceAmount, tourContext);

    setLoading(false);
  };

  const consumable = subscription.usesLedgers ? "credits" : "AI words";

  return (
    <StyledGraceCTA className={classNames("grace-cta", "show-coins", { sw2: isSW2 })}>
      <div className={"left-container"}>
        <img src={coinsURL.current} />
        {getGraceMessage(subscription, subscriptionData, consumable)}
      </div>

      <button
        disabled={loading}
        onClick={handleClaimGrace}
      >
        Claim {consumable}
      </button>
    </StyledGraceCTA>
  );
};

const GraceCTA = ({ subscription, uid, userData }) => {
  if (!subscription || !subscription.usesQuota || !uid) {
    return null;
  }

  const zeroLeft = subscription.totalQuota <= 0;
  const unclaimedGrace = !subscription.data.grace && zeroLeft && subscription.data.graceAmount > 0;

  if (!unclaimedGrace) {
    return null;
  }

  return (
    <GraceCTAComponent
      subscription={subscription}
      uid={uid}
      userData={userData}
    />
  );
};

export default GraceCTA;
